import React from "react";
import { useTranslation } from "react-i18next";
import "./index.css";

const LCA_PHASES_STD = ["A1","A2","A3","A4","A5","B1","B2","B3","B4","B5","B6","B7","C1","C2","C3","C4","D"];
const LCA_PHASES_C2C = ["A1","A2","A3","A4","A5","B1","B2","B3","B4","B5","B6","B7","C1","C2","C3","C4_tech","C4_bio","D"];

const PHASE_LABELS_STD = {
  A1: "Raw material supply",
  A2: "Transport",
  A3: "Manufacturing",
  A4: "Transport",
  A5: "Construction & Installation",
  B1: "Use",
  B2: "Maintenance",
  B3: "Repair",
  B4: "Replacement",
  B5: "Refurbishment",
  B6: "Operational energy use",
  B7: "Operational water use",
  C1: "De-construction & demolition",
  C2: "Transport",
  C3: "Waste processing",
  C4: "Disposal",
  D: "Reuse-recovery-recycling potential",
};

const PHASE_LABELS_C2C = {
  A1: "Material supply",
  A2: "Transport",
  A3: "Manufacturing",
  A4: "Transport",
  A5: "Construction & Installation",
  B1: "Use",
  B2: "Maintenance",
  B3: "Repair",
  B4: "Replacement",
  B5: "Refurbishment",
  B6: "Operational energy use",
  B7: "Operational water use",
  C1: "Deconstruction",
  C2: "Transport",
  C3: "Resource handling",
  C4_tech: "Technological reuse",
  C4_bio: "Biological reuse",
  D: "Substitution effects",
};

const PHASE_CODE_DISPLAY = {
  C4_tech: "C4a",
  C4_bio: "C4b",
};

const BR18_PHASES = { A1: true, A2: true, A3: true, A4: true, A5: true, B4: true, B6: true, C3: true, C4: true, D: true };

const SCOPE_OPTIONS = [
  { value: "cradle-to-gate", label: "Cradle to Gate" },
  { value: "cradle-to-cradle", label: "Cradle to Cradle" },
  { value: "cradle-to-grave", label: "Cradle to Grave" },
  { value: "br18", label: "BR18" },
  { value: "custom", label: "Custom" },
];

const LCAPhaseDisplay = ({ selectedPhases, lcaScope }) => {
  const { t } = useTranslation();

  const isCradleToCradle = lcaScope === "cradle-to-cradle";
  const phaseCodes = isCradleToCradle ? LCA_PHASES_C2C : LCA_PHASES_STD;
  const phaseLabels = isCradleToCradle ? PHASE_LABELS_C2C : PHASE_LABELS_STD;
  const totalCols = phaseCodes.length;
  const eolCols = isCradleToCradle ? 5 : 4;
  const gridStyle = { gridTemplateColumns: `repeat(${totalCols}, minmax(0, 1fr))` };

  return (
    <div className="lca-phase-display-container">
      <div className="lca-phase-display-section">
        <div className="lca-phase-display-header">
          <h3 className="lca-phase-display-title">
            {t("LCA_PHASE_SELECTION") || "Faseopdeling for LCA"}
          </h3>
          <span className="lca-phase-display-badge">
            {t("REFLECTS_SELECTION_ABOVE") || "Reflects selection above"}
          </span>
        </div>

        {/* Scope Selection – Read Only */}
        <div className="lca-phase-display-scope-section">
          <div className="lca-phase-display-scope-wrapper">
            <h4 className="lca-phase-display-scope-title">This calculation covers:</h4>
            <div className="lca-phase-display-scope-options">
              {SCOPE_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  className={`lca-phase-display-scope-option${lcaScope === opt.value ? " active" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={lcaScope === opt.value}
                    readOnly
                    disabled
                    className="lca-phase-display-scope-checkbox"
                  />
                  <span className="lca-phase-display-scope-label">{opt.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Phase Grid – Read Only */}
        <div className="lca-phase-display-grid-wrapper">
          {/* Main Category Headers */}
          <div className="lca-phase-display-category-headers" style={gridStyle}>
            <div className="lca-phase-display-category-header lca-phase-display-category-product" style={{ gridColumn: "span 5" }}>
              <div className="lca-phase-display-category-title">Product</div>
              <div className="lca-phase-display-category-subtitle">Construction process</div>
            </div>
            <div className="lca-phase-display-category-header lca-phase-display-category-use" style={{ gridColumn: "span 7" }}>
              <div className="lca-phase-display-category-title">Use</div>
            </div>
            <div className="lca-phase-display-category-header lca-phase-display-category-eol" style={{ gridColumn: `span ${eolCols}` }}>
              <div className="lca-phase-display-category-title">
                {isCradleToCradle ? "Transition" : "End of life"}
              </div>
            </div>
            <div className="lca-phase-display-category-header lca-phase-display-category-beyond" style={{ gridColumn: "span 1" }}>
              <div className="lca-phase-display-category-title lca-phase-display-category-beyond-title">
                {isCradleToCradle ? "Next System Effects" : "Beyond system"}
              </div>
            </div>
          </div>

          {/* Phase Labels */}
          <div className="lca-phase-display-labels-row" style={gridStyle}>
            {phaseCodes.map((phase) => {
              let categoryClass = "";
              if (["A1","A2","A3","A4","A5"].includes(phase)) categoryClass = "lca-phase-display-label-product";
              else if (["B1","B2","B3","B4","B5","B6","B7"].includes(phase)) categoryClass = "lca-phase-display-label-use";
              else if (["C1","C2","C3","C4","C4_tech","C4_bio"].includes(phase)) categoryClass = "lca-phase-display-label-eol";
              else categoryClass = "lca-phase-display-label-beyond";
              return (
                <div key={phase} className={`lca-phase-display-label-cell ${categoryClass}`}>
                  <div className="lca-phase-display-phase-label">{phaseLabels[phase]}</div>
                </div>
              );
            })}
          </div>

          {/* Phase Codes */}
          <div className="lca-phase-display-codes-row" style={gridStyle}>
            {phaseCodes.map((phase) => (
              <div key={phase} className="lca-phase-display-code-cell">
                <div className="lca-phase-display-phase-code">{PHASE_CODE_DISPLAY[phase] || phase}</div>
              </div>
            ))}
          </div>

          {/* Checkboxes – Read Only */}
          <div className="lca-phase-display-checkboxes-row" style={gridStyle}>
            {phaseCodes.map((phase) => (
              <div key={phase} className="lca-phase-display-checkbox-cell">
                <input
                  type="checkbox"
                  checked={selectedPhases?.[phase] || false}
                  readOnly
                  disabled
                  className="lca-phase-display-checkbox"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LCAPhaseDisplay;
